import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { apiJson } from '@/api/client'
import { savedFiltersQueryKey } from '../../hooks/useSavedFilters'
import type {
  AlertFilters,
  CreateSavedFilterPayload,
  SavedFilter,
} from '../../types'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  filters: AlertFilters
  onSaved: (saved: SavedFilter) => void
}

/**
 * AF-126 — "Save current filters as a new set" dialog.
 *
 * Hits POST /alerts/saved-filters; the optimistic UX is just the toast — we
 * intentionally wait for backend confirmation so name conflicts don't ghost
 * a tab into the UI.
 */
export function SaveFilterDialog({ open, onOpenChange, filters, onSaved }: Props) {
  const [name, setName] = useState('')
  const nameInputRef = useRef<HTMLInputElement | null>(null)
  const qc = useQueryClient()

  // Focus the name input on open. Radix Dialog focuses the first focusable
  // descendant by default (which would be the Cancel button); we override
  // here so analysts can type immediately.
  useEffect(() => {
    if (!open) return
    const id = window.setTimeout(() => nameInputRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [open])

  const create = useMutation({
    mutationFn: (payload: CreateSavedFilterPayload) =>
      apiJson<{ saved_filter: SavedFilter }>('/alerts/saved-filters', {
        method: 'POST',
        body: payload,
      }),
    onSuccess: ({ saved_filter }) => {
      toast.success(`Saved as "${saved_filter.name}"`)
      void qc.invalidateQueries({ queryKey: savedFiltersQueryKey })
      onSaved(saved_filter)
      onOpenChange(false)
      setName('')
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Couldn't save filter set"),
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (trimmed.length === 0) return
    create.mutate({ name: trimmed, filters })
  }

  const isEmpty = Object.keys(filters).length === 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save filter set</DialogTitle>
          <DialogDescription>
            Name this combination of filters so you can return to it from the tab bar.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="saved-name">Name</Label>
            <Input
              ref={nameInputRef}
              id="saved-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. NIP mismatches today"
              maxLength={60}
            />
          </div>
          {isEmpty && (
            <p className="rounded border border-status-pending/30 bg-status-pending/5 px-2 py-1.5 text-xs text-status-pending">
              No filters are currently active — saving an empty set is allowed but unusual.
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={name.trim().length === 0 || create.isPending}
            >
              {create.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
